import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil, combineLatest, debounceTime, switchMap } from 'rxjs';

import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { ProjectsService } from 'app/modules/apps/projects/services/projects.service';
import { MockDataService } from 'app/modules/apps/projects/services/mock-data.service';
import { MockUser, Project, MockClient, MockProduct } from 'app/modules/apps/projects/types/project.types';

export interface NavTreeNode {
    id: string;
    label: string;
    type: 'client' | 'product' | 'project' | 'new-link' | 'route-link';
    icon?: string;
    children?: NavTreeNode[];
    project?: Project;
    entityId?: string;
    route?: string;
    expanded: boolean;
    level: number;
}

@Component({
    selector: 'projects-nav-tree',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatTooltipModule, TranslocoDirective],
    templateUrl: './projects-nav-tree.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsNavTreeComponent implements OnInit, OnDestroy {
    private _router = inject(Router);
    private _service = inject(MockDataService);
    private _projectsService = inject(ProjectsService);
    private _userService = inject(UserService);
    private _cdr = inject(ChangeDetectorRef);
    private _transloco = inject(TranslocoService);
    private _destroy$ = new Subject<void>();

    tree: NavTreeNode[] = [];
    user: MockUser;
    private _realUser: User | null = null;
    private _allProjects: Project[] = [];

    ngOnInit(): void {
        // Initial fetch to populate BehaviorSubject
        this._projectsService.getProjects()
            .pipe(takeUntil(this._destroy$))
            .subscribe();

        // Subscribe to reactive project list — rebuilds tree on any mutation (debounced to prevent flicker)
        this._projectsService.projects$
            .pipe(
                debounceTime(500),
                takeUntil(this._destroy$),
            )
            .subscribe(projects => {
                this._allProjects = projects;
                this.tree = this._buildTree();
                this._cdr.markForCheck();
            });

        combineLatest([
            this._service.currentUser$,
            this._userService.user$,
        ])
            .pipe(takeUntil(this._destroy$))
            .subscribe(([user, realUser]) => {
                this.user = user;
                this._realUser = realUser;
                this.tree = this._buildTree();
                this._cdr.markForCheck();
            });

        // Rebuild tree when language changes (wait for translation file to load)
        this._transloco.langChanges$
            .pipe(
                switchMap(lang => this._transloco.selectTranslation(lang)),
                takeUntil(this._destroy$),
            )
            .subscribe(() => {
                this.tree = this._buildTree();
                this._cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    toggleNode(node: NavTreeNode, siblings?: NavTreeNode[]): void {
        if (node.expanded) {
            node.expanded = false;
        } else {
            // Accordion: collapse all siblings first
            if (siblings) {
                siblings.forEach(n => {
                    if (n !== node && n.type !== 'new-link') {
                        n.expanded = false;
                    }
                });
            }
            node.expanded = true;
        }
    }

    navigateToProject(project: Project): void {
        this._projectsService.markViewed(project.id).subscribe();
        this._router.navigate(['/projects', project.id]);
    }

    navigateToEntity(node: NavTreeNode): void {
        if (node.type === 'client') {
            this._router.navigate(['/projects/clients', node.entityId]);
        } else if (node.type === 'product') {
            this._router.navigate(['/projects/products', node.entityId]);
        }
    }

    navigateToNew(nodeId: string): void {
        if (nodeId === 'new-client') {
            this._router.navigate(['/projects/clients/new']);
        } else if (nodeId.startsWith('new-product')) {
            this._router.navigate(['/projects/products/new']);
        } else {
            this._router.navigate(['/projects/new']);
        }
    }

    navigateToRoute(route: string): void {
        this._router.navigate([route]);
    }

    navigateToList(nodeId: string): void {
        if (nodeId === 'my-clients') {
            this._router.navigate(['/projects/clients']);
        } else if (nodeId === 'my-products') {
            this._router.navigate(['/projects/products']);
        } else if (nodeId === 'my-projects') {
            this._router.navigate(['/projects/list']);
        }
    }

    getItemCount(node: NavTreeNode): number {
        // Top-level "My Clients" wrapper — count is number of child client nodes
        if (node.id === 'my-clients') {
            return this._service.getUserClients().length;
        }
        // Top-level "My Products" wrapper — count is total projects across all products
        if (node.id === 'my-products') {
            return this._getAllProjectsForCurrentUser();
        }
        // Top-level "My Projects" wrapper — count is total projects
        if (node.id === 'my-projects') {
            return this._getAllProjectsForRegularUser().length;
        }
        if (node.type === 'client') {
            return this._filterArchived(
                this._allProjects.filter((p: Project) => p.clientId === node.entityId)
            ).length;
        }
        if (node.type === 'product') {
            return this._filterArchived(this._service.getProjectsByProduct(node.entityId!)).length;
        }
        return 0;
    }

    hasUnseenChildren(node: NavTreeNode): boolean {
        // Top-level wrappers — check if any child has unseen
        if (node.id === 'my-clients' || node.id === 'my-products' || node.id === 'my-projects') {
            return this._allProjects.some(
                (p: Project) => this._service.hasUnseenStatusChange(p)
            );
        }
        if (node.type === 'client') {
            return this._service.hasUnseenInClient(node.entityId!);
        }
        if (node.type === 'product') {
            return this._service.hasUnseenInProduct(node.entityId!);
        }
        return false;
    }

    /** Show badge for any node that has children (including top-level wrappers) */
    shouldShowBadge(node: NavTreeNode): boolean {
        return node.children != null && node.children.length > 0 && this.getItemCount(node) > 0;
    }

    getTypeIcon(project: Project): string {
        const pt = this._service.getProjectTypeById(project.projectTypeId);
        return pt?.icon || 'heroicons_outline:folder';
    }

    getTypeColor(project: Project): string {
        const pt = this._service.getProjectTypeById(project.projectTypeId);
        return pt?.iconColor || 'text-gray-500';
    }

    getTypeTooltip(project: Project): string {
        const pt = this._service.getProjectTypeById(project.projectTypeId);
        return pt?.name || '';
    }

    getStatusIcon(project: Project): string {
        return this._service.getStatusIcon(project.status);
    }

    getStatusColor(project: Project): string {
        return this._service.getStatusColor(project);
    }

    getStatusTooltip(project: Project): string {
        const keyMap: Record<string, string> = {
            pending: 'projects.status.pending',
            queued: 'projects.status.queued',
            working: 'projects.status.working',
            need_interaction: 'projects.status.needInteraction',
            ready_for_acceptance: 'projects.status.readyForAcceptance',
            finished: 'projects.status.finished',
            archived: 'projects.status.archived',
            on_hold: 'projects.status.onHold',
        };
        const label = this._transloco.translate(keyMap[project.status] || project.status);
        if (project.status === 'on_hold' && project.hold_reason) {
            return `${label}: ${project.hold_reason}`;
        }
        return label;
    }

    // --- Private ---

    private _buildTree(): NavTreeNode[] {
        const userType = this.user?.type;
        let nodes: NavTreeNode[];
        if (userType === 'attorney') {
            nodes = this._buildAttorneyTree();
        } else if (userType === 'enterprise') {
            nodes = this._buildEnterpriseTree();
        } else {
            nodes = this._buildRegularTree();
        }

        // Admin-only Dashboard folder
        if (this._realUser?.role === 'admin') {
            nodes.push(this._buildDashboardFolder());
        }

        return nodes;
    }

    private _buildDashboardFolder(): NavTreeNode {
        return {
            id: 'dashboard',
            label: 'Dashboard',
            type: 'client',
            icon: 'heroicons_outline:chart-pie',
            expanded: false,
            level: 0,
            entityId: '',
            children: [
                {
                    id: 'dashboard-analytics',
                    label: 'Analytics',
                    type: 'route-link',
                    icon: 'heroicons_outline:presentation-chart-line',
                    route: '/dashboard/analytics',
                    expanded: false,
                    level: 1,
                },
                {
                    id: 'dashboard-health',
                    label: 'Health',
                    type: 'route-link',
                    icon: 'heroicons_outline:heart',
                    route: '/dashboard/health',
                    expanded: false,
                    level: 1,
                },
                {
                    id: 'dashboard-notifications',
                    label: 'Notifications',
                    type: 'route-link',
                    icon: 'heroicons_outline:bell-alert',
                    route: '/dashboard/notifications',
                    expanded: false,
                    level: 1,
                },
                {
                    id: 'dashboard-config',
                    label: 'Configuration',
                    type: 'route-link',
                    icon: 'heroicons_outline:cog-6-tooth',
                    route: '/dashboard/config',
                    expanded: false,
                    level: 1,
                },
            ],
        };
    }

    /** Attorney: Clients > Products > Projects (3 levels) */
    private _buildAttorneyTree(): NavTreeNode[] {
        const clients = this._service.getUserClients();
        const nodes: NavTreeNode[] = [];

        if (clients.length === 0) {
            nodes.push({
                id: 'new-client',
                label: this._transloco.translate('projects.navTree.newClient'),
                type: 'new-link',
                icon: 'heroicons_outline:plus',
                expanded: false,
                level: 0,
            });
            return nodes;
        }

        // "My Clients" folder
        const clientNodes: NavTreeNode[] = clients.map((client, i) => this._buildClientNode(client, i === 0));

        // Orphan projects (no client assigned)
        const orphans = this._filterArchived(this._service.getOrphanProjects());
        if (orphans.length > 0) {
            clientNodes.push(this._buildOrphanFolder(orphans, 1));
        }

        // "New Client" link inside folder
        clientNodes.push({
            id: 'new-client',
            label: this._transloco.translate('projects.navTree.newClient'),
            type: 'new-link',
            icon: 'heroicons_outline:plus',
            expanded: false,
            level: 1,
        });

        nodes.push({
            id: 'my-clients',
            label: this._transloco.translate('projects.navTree.myClients'),
            type: 'client',
            icon: 'heroicons_outline:folder',
            children: clientNodes,
            expanded: true,
            level: 0,
            entityId: '',
        });

        return nodes;
    }

    private _buildClientNode(client: MockClient, isFirst: boolean = false): NavTreeNode {
        const products = this._service.getProducts(client.id);
        const productNodes: NavTreeNode[] = products.map((prod, i) => this._buildProductNode(prod, 2, isFirst && i === 0));

        // Projects under this client without a product
        const projectsUnderClient = this._getProjectsForClientWithoutProduct(client.id);
        if (projectsUnderClient.length > 0) {
            productNodes.push(this._buildOrphanFolder(projectsUnderClient, 2));
        }

        // "New Product" link inside each client
        productNodes.push({
            id: `new-product-${client.id}`,
            label: this._transloco.translate('projects.navTree.newProduct'),
            type: 'new-link',
            icon: 'heroicons_outline:plus',
            expanded: false,
            level: 2,
        });

        return {
            id: `client-${client.id}`,
            label: client.name,
            type: 'client',
            icon: 'heroicons_outline:building-office',
            children: productNodes,
            entityId: client.id,
            expanded: isFirst,
            level: 1,
        };
    }

    private _buildProductNode(product: MockProduct, level: number, isExpanded: boolean = false): NavTreeNode {
        const projects = this._sortByDate(this._filterArchived(this._service.getProjectsByProduct(product.id)));
        const projectNodes: NavTreeNode[] = projects.map(p => ({
            id: `project-${p.id}`,
            label: p.name,
            type: 'project' as const,
            project: p,
            expanded: false,
            level: level + 1,
        }));

        // "New Project" link inside each product
        projectNodes.push({
            id: `new-project-${product.id}`,
            label: this._transloco.translate('projects.navTree.newProject'),
            type: 'new-link',
            icon: 'heroicons_outline:plus',
            expanded: false,
            level: level + 1,
        });

        return {
            id: `product-${product.id}`,
            label: product.name,
            type: 'product',
            icon: 'heroicons_outline:cube',
            children: projectNodes,
            entityId: product.id,
            expanded: isExpanded,
            level,
        };
    }

    /** Enterprise: Products > Projects (2 levels) */
    private _buildEnterpriseTree(): NavTreeNode[] {
        const products = this._service.getUserProducts();
        const nodes: NavTreeNode[] = [];

        if (products.length === 0) {
            nodes.push({
                id: 'new-product',
                label: this._transloco.translate('projects.navTree.newProduct'),
                type: 'new-link',
                icon: 'heroicons_outline:plus',
                expanded: false,
                level: 0,
            });
            return nodes;
        }

        // "My Products" folder
        const productNodes: NavTreeNode[] = products.map((prod, i) => this._buildProductNode(prod, 1, i === 0));

        // Orphan projects
        const orphans = this._filterArchived(this._service.getOrphanProjects());
        if (orphans.length > 0) {
            productNodes.push(this._buildOrphanFolder(orphans, 1));
        }

        // "New Product" link inside folder
        productNodes.push({
            id: 'new-product',
            label: this._transloco.translate('projects.navTree.newProduct'),
            type: 'new-link',
            icon: 'heroicons_outline:plus',
            expanded: false,
            level: 1,
        });

        nodes.push({
            id: 'my-products',
            label: this._transloco.translate('projects.navTree.myProducts'),
            type: 'product',
            icon: 'heroicons_outline:folder',
            children: productNodes,
            expanded: true,
            level: 0,
            entityId: '',
        });

        return nodes;
    }

    /** Regular: Projects (1 level) */
    private _buildRegularTree(): NavTreeNode[] {
        // For regular user, all projects are shown flat (no hierarchy)
        const allProjects = this._sortByDate(this._getAllProjectsForRegularUser());
        const nodes: NavTreeNode[] = [];

        if (allProjects.length === 0) {
            nodes.push({
                id: 'new-project',
                label: this._transloco.translate('projects.navTree.newProject'),
                type: 'new-link',
                icon: 'heroicons_outline:plus',
                expanded: false,
                level: 0,
            });
            return nodes;
        }

        // "My Projects" folder
        const projectNodes: NavTreeNode[] = allProjects.map(p => ({
            id: `project-${p.id}`,
            label: p.name,
            type: 'project' as const,
            project: p,
            expanded: false,
            level: 1,
        }));

        // "New Project" link inside folder
        projectNodes.push({
            id: 'new-project',
            label: this._transloco.translate('projects.navTree.newProject'),
            type: 'new-link',
            icon: 'heroicons_outline:plus',
            expanded: false,
            level: 1,
        });

        nodes.push({
            id: 'my-projects',
            label: this._transloco.translate('projects.navTree.myProjects'),
            type: 'product',  // use folder type, not project leaf
            icon: 'heroicons_outline:folder',
            children: projectNodes,
            expanded: true,
            level: 0,
            entityId: '',
        });

        return nodes;
    }

    private _buildOrphanFolder(projects: Project[], level: number): NavTreeNode {
        const projectNodes: NavTreeNode[] = this._sortByDate(projects).map(p => ({
            id: `project-${p.id}`,
            label: p.name,
            type: 'project' as const,
            project: p,
            expanded: false,
            level: level + 1,
        }));

        return {
            id: 'orphan-projects',
            label: 'Unassigned',
            type: 'product',
            icon: 'heroicons_outline:folder',
            children: projectNodes,
            expanded: false,
            level,
            entityId: '',
        };
    }

    private _getProjectsForClientWithoutProduct(clientId: string): Project[] {
        // Projects that belong to this client but have no product
        return this._sortByDate(this._filterArchived(
            this._allProjects.filter(
                (p: Project) => p.clientId === clientId && !p.productId
            )
        ));
    }

    private _getAllProjectsForCurrentUser(): number {
        return this._filterArchived(this._allProjects).length;
    }

    private _getAllProjectsForRegularUser(): Project[] {
        return this._filterArchived(this._allProjects);
    }

    private _filterArchived(projects: Project[]): Project[] {
        return projects.filter(p => p.status !== 'archived');
    }

    /** Sort projects by updated_at descending (most recently updated first) */
    private _sortByDate(projects: Project[]): Project[] {
        return [...projects].sort((a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
    }
}
